import React, { useState, useEffect } from "react";
import "./css/RecentSimulations.css";
import { getCookie } from "../utils/CookieUtil";
import { formatTimestamp } from "../utils/DateTimeUtil";
import APIService from "../services/APIService";
import RecentSimulationsAPI from "../services/RecentSimulationsAPI";
import UserAPI from "../services/UserAPI";
import { useUser } from "../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faSms,
  faPhone,
  faShare,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * RecentSimulations component for displaying recent messages sent grouped by channel.
 * Displays email, SMS, voice, and social media messages with details and timestamps.
 */
const RecentSimulations = () => {
  const { selectedUser: contextSelectedUser, loggedInUser } = useUser();
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUuid, setCurrentUuid] = useState(null);
  const [channelCounts, setChannelCounts] = useState({});
  const [groupedMessages, setGroupedMessages] = useState({
    email: [],
    sms: [],
    voice: [],
    "social-media": [],
  });

  // Channel configuration for display
  const channelConfig = {
    email: {
      icon: faEnvelope,
      label: "Email Messages",
      color: "#007bff",
    },
    sms: {
      icon: faSms,
      label: "SMS Messages",
      color: "#28a745",
    },
    voice: {
      icon: faPhone,
      label: "Voice Messages",
      color: "#ffc107",
    },
    "social-media": {
      icon: faShare,
      label: "Social Media Messages",
      color: "#6f42c1",
    },
  };

  // Get current user and fetch recent simulations
  useEffect(() => {
    const getCurrentUserAndFetchSimulations = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use selectedUser from UserContext, fallback to loggedInUser if no selection
        const currentUser = contextSelectedUser || loggedInUser;

        if (!currentUser || !currentUser.uuid) {
          setError(
            "No user selected. Please select a user from the User List or log in.",
          );
          setLoading(false);
          return;
        }

        console.log(
          "[RecentSimulations] Loading simulations for user:",
          currentUser.uuid,
          currentUser.email,
        );

        setCurrentUuid(currentUser.uuid);

        // Fetch recent messages for authenticated user
        await fetchRecentMessages();

        // Fetch channel counts for better UI display
        await fetchChannelCounts();
      } catch (error) {
        console.error("Error loading user or recent simulations:", error);
        setError("Failed to load recent simulations. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    getCurrentUserAndFetchSimulations();
  }, [contextSelectedUser, loggedInUser]); // Re-run when selectedUser or loggedInUser changes

  // Transform backend CommunicationSimulationDTO to frontend message format
  const transformActivityToMessage = (activity) => {
    console.log("Transforming CommunicationSimulationDTO activity:", activity);
    console.log("Available activity fields:", Object.keys(activity));
    console.log("Subject field value:", activity.subject);
    console.log("Channel type:", activity.channelType);

    // Extract recipient from properties if available, otherwise use email/phone
    let recipient = "Unknown recipient";
    let sophisticationLevel = "Basic";

    // Handle subject extraction - prioritize backend subject field
    let messageSubject = "No Subject";
    if (activity.subject && activity.subject.trim() !== "") {
      messageSubject = activity.subject;
    } else {
      // Fallback to generate default subject based on channel type
      const channelType = normalizeChannelType(activity.channelType);
      switch (channelType) {
        case "email":
          messageSubject = activity.subject;
          break;
        case "sms":
          messageSubject = "Simulation SMS";
          break;
        case "voice":
          messageSubject = "Simulation Voice Call";
          break;
        case "social-media":
          messageSubject = "Simulation Social Media Message";
          break;
        default:
          messageSubject = `Simulation ${channelType} Message`;
      }
    }

    // Handle content extraction
    let messageContent =
      activity.contentPlainText ||
      activity.contentHtml ||
      activity.contentSnippet ||
      activity.content ||
      "No content available";

    try {
      if (activity.properties) {
        const props =
          typeof activity.properties === "string"
            ? JSON.parse(activity.properties)
            : activity.properties;

        console.log("Parsed properties:", props);

        // Extract recipient information
        recipient =
          props.recipient ||
          props.sendTo ||
          props.recipientPhone ||
          props.recipientEmail ||
          recipient;

        // Extract sophistication level
        sophisticationLevel =
          activity.sophisticationLevel ||
          props.sophisticationLevel ||
          sophisticationLevel;

        // Only override subject if the current one is still default and properties has a subject
        if (messageSubject === "No Subject" && props.subject) {
          messageSubject = props.subject;
        }

        // Only override content if current content is default and properties has content
        if (messageContent === "No content available") {
          messageContent =
            props.content ||
            props.messageContent ||
            props.contentSnippet ||
            props.plainTextContent ||
            props.originalContent ||
            messageContent;
        }
      }
    } catch (e) {
      console.warn("Error parsing activity properties:", e);
    }

    // If no recipient in properties, use email or phone from activity
    if (recipient === "Unknown recipient") {
      recipient =
        activity.email ||
        activity.phone ||
        activity.recipientEmail ||
        "Unknown recipient";
    }

    // Extract context from activity type or properties
    let communicationContext = activity.communicationContext || "general";
    try {
      if (activity.properties) {
        const props =
          typeof activity.properties === "string"
            ? JSON.parse(activity.properties)
            : activity.properties;
        communicationContext =
          props.communicationContext || props.context || communicationContext;
      }
    } catch (e) {
      // Keep default if parsing fails
    }

    // Use the most recent timestamp available (prioritize sentAt, then createdAt)
    let timestamp = new Date();
    if (activity.sentAt) {
      timestamp = new Date(activity.sentAt);
    } else if (activity.createdAt) {
      timestamp = new Date(activity.createdAt);
    }

    console.log("Final transformed message data:", {
      id: activity.simulationId || activity.activityId || activity.id,
      subject: messageSubject,
      content: messageContent,
      recipient: recipient,
      sophisticationLevel: sophisticationLevel,
      communicationContext: communicationContext,
      channelType: normalizeChannelType(activity.channelType),
    });

    return {
      id: activity.simulationId || activity.activityId || activity.id,
      messageId: activity.messageId
        ? String(activity.messageId)
        : `SIM-${activity.simulationId || activity.activityId || activity.id}`,
      subject: messageSubject,
      content: messageContent,
      contentSnippet: messageContent, // Keep both for compatibility
      communicationChannel: normalizeChannelType(activity.channelType),
      communicationContext: communicationContext,
      sophisticationLevel: sophisticationLevel,
      timestamp: timestamp,
      status: activity.status || "sent",
      recipient: recipient,
      // Additional backend fields for reference
      email: activity.email,
      phone: activity.phone,
      senderId: activity.senderId,
      externalId: activity.externalId,
      trackingId: activity.trackingId,
      createdAt: activity.createdAt,
      sentAt: activity.sentAt,
      deliveredAt: activity.deliveredAt,
    };
  };

  // Normalize channel type from backend to match frontend expectations
  const normalizeChannelType = (channelType) => {
    if (!channelType) return "email";

    const channel = channelType.toLowerCase().trim();

    // Handle multiple channels (comma-separated) - take the first one for grouping
    const firstChannel = channel.split(",")[0].trim();

    // Map backend channel types to frontend channel types
    const channelMap = {
      email: "email",
      sms: "sms",
      voice: "voice",
      "social-media": "social-media",
      "social media": "social-media",
      phone: "voice",
      call: "voice",
      message: "sms",
      text: "sms",
    };

    return channelMap[firstChannel] || "email";
  };

  // Fetch recent messages from the API
  const fetchRecentMessages = async () => {
    try {
      console.log(
        "Fetching recent simulations for authenticated user (self-access)",
      );

      // Get target UUID from context or logged-in user
      const targetUuid = contextSelectedUser?.uuid || loggedInUser?.uuid;

      if (!targetUuid) {
        console.error("No user UUID available for fetching simulations");
        setError("No user selected. Please select a user first.");
        setRecentMessages([]);
        groupMessagesByChannel([]);
        return;
      }

      // Determine if viewing self or managed user
      // Pass null for self-access endpoint, targetUuid for admin endpoint
      const simulations = await RecentSimulationsAPI.getSimulationsForUser(
        targetUuid === loggedInUser?.uuid ? null : targetUuid,
      );

      console.log("Recent simulations response:", simulations);

      if (!Array.isArray(simulations)) {
        console.warn("Expected array of simulations, got:", typeof simulations);
        setRecentMessages([]);
        groupMessagesByChannel([]);
        return;
      }

      // Transform backend DTOs to frontend message format
      const transformedMessages = simulations.map(transformActivityToMessage);

      console.log("Transformed messages:", transformedMessages);

      setRecentMessages(transformedMessages);
      groupMessagesByChannel(transformedMessages);
    } catch (error) {
      console.error("Error fetching recent messages:", error);
      // If API fails, fall back to empty state rather than throwing
      // This provides better user experience
      setRecentMessages([]);
      groupMessagesByChannel([]);
    }
  };
  // Fetch channel counts from the API
  const fetchChannelCounts = async () => {
    try {
      console.log(
        "Fetching channel counts for authenticated user (self-access)",
      );

      // Use the centralized RecentSimulationsAPI without UUID for self-access
      const counts = await RecentSimulationsAPI.getChannelCountsForUser();

      console.log("Channel counts response:", counts);

      setChannelCounts(counts || {});
    } catch (error) {
      console.error("Error fetching channel counts:", error);
      // Don't throw error here, just set empty counts
      setChannelCounts({});
    }
  };

  // Group messages by communication channel
  const groupMessagesByChannel = (messages) => {
    const grouped = {
      email: [],
      sms: [],
      voice: [],
      "social-media": [],
    };

    messages.forEach((message) => {
      const channel = message.communicationChannel?.toLowerCase();
      if (grouped[channel]) {
        grouped[channel].push(message);
      }
    });

    // Sort each group by timestamp (most recent first)
    Object.keys(grouped).forEach((channel) => {
      grouped[channel].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      );
    });
    setGroupedMessages(grouped);
  };

  // Render a single message
  const renderMessage = (message) => (
    <div key={message.id} className="message-item">
      <div className="message-header">
        <h4 className="message-subject">{message.subject || "No Subject"}</h4>
        <span className="message-time">
          {formatTimestamp(message.timestamp)}
        </span>
      </div>
      <div className="message-details">
        <p className="message-content">
          {message.contentSnippet && message.contentSnippet.length > 150
            ? `${message.contentSnippet.substring(0, 150)}...`
            : message.contentSnippet || "No content available"}
        </p>
        <div className="message-meta">
          <span className="message-context">
            Context: {message.communicationContext || "general"}
          </span>
          <span className="message-level">
            Level: {message.sophisticationLevel || "Basic"}
          </span>
          <span className="message-recipient">
            To: {message.recipient || "Unknown recipient"}
          </span>
          {message.status && (
            <span className="message-status">Status: {message.status}</span>
          )}
        </div>
      </div>
    </div>
  );

  // Render messages for a specific channel
  const renderChannelSection = (channel) => {
    const messages = groupedMessages[channel] || [];
    const config = channelConfig[channel];

    // Get count from API response, fallback to messages length
    const apiCount = channelCounts[channel] || channelCounts[config.label] || 0;
    const displayCount = Math.max(messages.length, apiCount);

    if (displayCount === 0) {
      return null;
    }

    return (
      <div key={channel} className="channel-section">
        <div className="channel-header" style={{ color: config.color }}>
          <FontAwesomeIcon icon={config.icon} className="channel-icon" />
          <h3>{config.label}</h3>
          <span className="message-count">({displayCount})</span>
        </div>
        <div className="channel-messages">
          {messages.length > 0 ? (
            messages.map(renderMessage)
          ) : (
            <div className="no-messages-in-channel">
              <p>
                No recent {config.label.toLowerCase()} found in current view
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="recent-simulations-container">
        <LoadingSpinner message="Loading recent simulations..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-simulations-container">
        <div className="error-state">
          <h2>Recent Simulations</h2>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  const totalMessages = recentMessages.length;

  return (
    <div className="recent-simulations-container">
      <div className="header-section">
        <div className="title-section">
          <FontAwesomeIcon icon={faHistory} className="title-icon" />
          <h2>Recent Simulations</h2>
        </div>
        <div className="summary-section">
          <span className="total-count">
            {totalMessages} total messages sent
          </span>
        </div>
      </div>

      {totalMessages === 0 ? (
        <div className="no-messages">
          <p>No recent message simulations found.</p>
          <p>
            Messages sent from the Simulation Messages section will appear here.
          </p>
        </div>
      ) : (
        <div className="simulations-content">
          {Object.keys(channelConfig).map(renderChannelSection)}
        </div>
      )}
    </div>
  );
};

export default RecentSimulations;
